import api from '../../../Api/axiosInstance';
import { Crypter } from '../../../Functions/Security/Crypter';
import { encryptBody } from './kycCrypto';
import Data from '../../../Data/Data.json';
import type { DemandeKycDocumentRow, DemandeKycDocumentUploadDto } from '../../../Models/DemandeKyc';

export const DOCUMENT_ACCEPT = '.pdf,.png,.jpg,.jpeg,.doc,.docx';
export const DOCUMENT_MAX_MO = 5;

export function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    // On ne garde que la partie base64 (le backend fait Convert.FromBase64String,
    // le préfixe "data:...;base64," lui ferait perdre pied).
    reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
    reader.onerror = () => reject(reader.error);
  });
}

export function isFileTooLarge(file: File): boolean {
  return file.size / (1024 * 1024) > DOCUMENT_MAX_MO;
}

function decryptErrorMessage(error: any, fallback: string): string {
  try {
    const decoded = JSON.parse(Crypter.decrypt(error.response.data)).Value;
    return decoded?.Message ?? fallback;
  } catch {
    return fallback;
  }
}

export async function fetchKycDocuments(idDemande: number | string): Promise<DemandeKycDocumentRow[]> {
  try {
    const response = await api.get(Data.ApiUrl.KycDocuments, {
      headers: { CypherIdDemande: Crypter.encrypt(String(idDemande)), 'Content-Type': 'application/json' },
    });
    const decoded = Crypter.decrypt(response.data);
    const parsed = JSON.parse(decoded);
    const payload = parsed?.Value ?? parsed;
    return payload?.StatutCode === 200 ? (payload.Result?.Table ?? payload.Result ?? []) : [];
  } catch (error: any) {
    if (error?.response?.status !== 404) throw error;
    return [];
  }
}

export async function uploadKycDocument(
  idDemande: number | string,
  uploadePar: string,
  file: File,
): Promise<{ ok: boolean; message?: string }> {
  const base64 = await readFileAsBase64(file);
  const extension = file.name.includes('.') ? file.name.split('.').pop() ?? '' : '';
  const dto: DemandeKycDocumentUploadDto = {
    IdDemande: idDemande,
    NomFichierOriginal: file.name,
    Extension: extension,
    TypeMime: file.type || 'application/octet-stream',
    UploadePar: uploadePar,
    Base64File: base64,
  };
  try {
    const response = await api.post(Data.ApiUrl.KycUploadDocument, encryptBody(dto), {
      headers: { 'Content-Type': 'application/json' },
    });
    const decoded = Crypter.decrypt(response.data);
    const parsed = JSON.parse(decoded);
    const payload = parsed?.Value ?? parsed;
    if (payload?.StatutCode === 200) return { ok: true };
    return { ok: false, message: payload?.Message ?? "Impossible d'ajouter ce document." };
  } catch (error: any) {
    return { ok: false, message: decryptErrorMessage(error, "Impossible d'ajouter ce document.") };
  }
}

export async function deleteKycDocument(idDocument: number | string): Promise<{ ok: boolean; message?: string }> {
  try {
    const response = await api.delete(Data.ApiUrl.KycDocument, {
      headers: { CypherIdDocument: Crypter.encrypt(String(idDocument)), 'Content-Type': 'application/json' },
    });
    const decoded = Crypter.decrypt(response.data);
    const parsed = JSON.parse(decoded);
    const payload = parsed?.Value ?? parsed;
    if (payload?.StatutCode === 200) return { ok: true };
    return { ok: false, message: payload?.Message ?? 'Impossible de retirer ce document.' };
  } catch {
    return { ok: false, message: 'Impossible de retirer ce document.' };
  }
}
